'use client'

import { useEffect } from 'react'
import { ArrowRightIcon, ShoppingBagIcon } from '@heroicons/react/24/outline'
import { Price } from '@/General/Price'
import { Button } from '@/General/Button'
import { Modal } from '@/Modal'

export const ChooseOptionModal = ({
  productName,
  options,
  selectedOptionId,
  quantity = 1,
  showOptions = true,
  showQuantitySelector = false,
  onClose,
  onOptionSelect,
  onQuantityChange,
  onAddToCart,
  isOpen,
  isPreorder = false,
}: ChooseOptionModalProps) => {
  // Filter out components from options
  const nonComponentOptions = options.filter(option => {
    const productTypeName = option.productType
    return productTypeName?.toLowerCase() !== 'component'
  })
  const hasValidSelectedOption = nonComponentOptions.some(
    option => option?.id === selectedOptionId
  )

  // Auto-select first in-stock option when modal opens and no option is selected;
  // if none in stock, still select first option so the UI is never stuck with no choice.
  useEffect(() => {
    if (isOpen && (!selectedOptionId || !hasValidSelectedOption)) {
      const firstInStockOption = nonComponentOptions.find(
        option => option?.inStock
      )
      const fallback =
        firstInStockOption ??
        nonComponentOptions.find(option => option?.id)
      if (fallback?.id) {
        onOptionSelect(fallback.id)
      }
    }
  }, [
    isOpen,
    selectedOptionId,
    hasValidSelectedOption,
    nonComponentOptions,
    onOptionSelect,
    isPreorder,
  ])

  const selectedOption = nonComponentOptions.find(
    option => option?.id === selectedOptionId
  )
  const requiresOptionSelection = showOptions && nonComponentOptions.length > 0
  const selectedIsUnavailable =
    selectedOption && !selectedOption.inStock && !isPreorder
  const isDisabled =
    (requiresOptionSelection &&
      (!selectedOptionId || selectedIsUnavailable)) ||
    quantity < 1

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <div className="relative">
        <div className="relative bg-gradient-to-br from-blue-50 to-indigo-50 px-6 pt-8 pb-6 overflow-hidden">
          <div className="flex flex-col items-center">
            <div className="mt-4 text-center transition-all duration-200 opacity-100">
              <h3 className="text-2xl font-bold text-gray-900 mb-1">
                {productName}
              </h3>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-6">
          {requiresOptionSelection && (
            <div className="space-y-3">
              {nonComponentOptions.map(option => (
                <div
                  key={option?.id}
                  className={`px-4 py-2 rounded-xl border-2 transition-all duration-200 cursor-pointer ${
                    selectedOptionId === option?.id
                      ? 'border-blue-500 bg-blue-50 shadow-md'
                      : !option?.inStock && !isPreorder
                        ? 'border-gray-200 bg-gray-50 hover:border-gray-300'
                        : 'border-gray-200 hover:border-gray-300'
                  }`}
                  onClick={() => option?.id && onOptionSelect(option.id)}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-gray-900">
                          {option?.description}
                        </span>
                        <div className="text-right">
                          {option?.price !== null &&
                            option?.price !== undefined &&
                            typeof option.price === 'number' && (
                              <Price
                                price={option.price}
                                salePrice={option.salePrice}
                                priceIncVat={option.priceIncVat}
                                salePriceIncVat={option.salePriceIncVat}
                                taxType={option.taxType}
                                currencySymbol={option.currencySymbol || '$'}
                                currencyCode={option.currencyCode}
                                size="lg"
                              />
                            )}
                        </div>
                      </div>
                      {!option?.inStock && !isPreorder && (
                        <p className="text-sm text-red-500 mt-1">Out of stock</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {showQuantitySelector && (
            <div className="mt-4">
              <label
                htmlFor="deck-quantity"
                className="block text-sm font-medium text-gray-700 mb-2"
              >
                Quantity
              </label>
              <select
                id="deck-quantity"
                className="w-full rounded-md border border-gray-300 px-3 py-2"
                value={quantity}
                onChange={e => onQuantityChange(Number(e.target.value))}
              >
                {Array.from({ length: 12 }, (_, i) => i + 1).map(qty => (
                  <option key={qty} value={qty}>
                    {qty}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="mt-4">
            <Button
              className="w-full"
              onClick={onAddToCart}
              disabled={isDisabled}
            >
              <ShoppingBagIcon className="w-5 h-5 mr-2 group-hover:animate-bounce" />
              Add to cart
              <ArrowRightIcon className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform duration-200" />
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  )
}

interface ChooseOptionModalProps {
  productName: string
  options: {
    id: number
    description: string
    price: number
    salePrice?: number | null
    priceIncVat?: number | null
    salePriceIncVat?: number | null
    taxType?: number | null
    currencySymbol: string
    currencyCode?: string | null
    inStock: boolean
    productType: string
    needsShipping: boolean
  }[]
  selectedOptionId: number | null
  quantity?: number
  showOptions?: boolean
  showQuantitySelector?: boolean
  onClose: () => void
  onOptionSelect: (optionId: number) => void
  onQuantityChange: (quantity: number) => void
  onAddToCart: () => void
  isOpen: boolean
  isPreorder?: boolean
}
